import { usePageContentNoProxy } from "@RHCommerceDev/custom-providers/LocationProvider";
import {
  phoneAtom,
  useSetAuthenticationDialogCurrentScreenAtom,
  useSetIsAuthenticationDialogAtom
} from "@RHCommerceDev/hooks/atoms";
import { Button } from "@RHCommerceDev/unstyled-ui-components/Button";
import { Typography } from "@RHCommerceDev/unstyled-ui-components/Typography";

import { useStytch } from "@stytch/react";
import StytchPhoneField from "@RHCommerceDev/component-standard-fields/StytchPhoneTextField";
import { useSetMobileAuthAtomAtom } from "@RHCommerceDev/hooks/atoms";
import useAsync from "hooks/useAsync";
import { useSetAtom } from "jotai";
import * as React from "react";
import { useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import memoize from "utils/memoize";
import useLocale from "hooks/useLocale";

const MobileAuthentication = () => {
  const form = useForm<PhoneInputFormType>({
    defaultValues: {
      phone: ""
    }
  });
  const { pageContent } = usePageContentNoProxy();
  const stytchData = pageContent?.emailSignupContent;
  const [errorMessage, setErrorMessage] = useState("");
  const setMobileAuthData = useSetMobileAuthAtomAtom();
  const setPhone = useSetAtom(phoneAtom);
  const setOpenAuthenticationDialog = useSetIsAuthenticationDialogAtom();
  const locale = useLocale();
  const setAuthenticationDialogCurrentScreen =
    useSetAuthenticationDialogCurrentScreenAtom();

  const stytchClient = useStytch();

  const { run, loading } = useAsync(async ({ phone }) => {
    try {
      const phoneAuthDetails = await stytchClient.otps.sms.send(phone, {
        expiration_minutes: 5,
        ...(locale.toLowerCase().includes("fr") && { locale: "fr" })
      });

      setMobileAuthData(phoneAuthDetails);
      setAuthenticationDialogCurrentScreen("SmsCodeHandler");
    } catch (error: any) {
      if (stytchData?.Stytch_Error_Object.hasOwnProperty(error.error_type)) {
        setErrorMessage(stytchData?.Stytch_Error_Object[error.error_type]);
      } else {
        console.error("error", error);
      }
    }
  });

  type PhoneInputFormType = {
    phone: any;
  };

  const onSubmit = React.useCallback(
    async (formValue: PhoneInputFormType) => {
      const { phone } = formValue;
      setPhone(phone);
      await run({ phone });
    },
    [run, setPhone]
  );

  const handleCloseModal = () => {
    setOpenAuthenticationDialog(false);
  };
  const formValues = form.watch();
  const isFormComplete = Object.values(formValues).every(value => {
    if (typeof value === "string") {
      return value.trim() !== "";
    }
    return false;
  });

  return (
    <div className="flex flex-col items-center">
      <Typography variant="h1" className="w-full font-primary-rh-thin">
        {stytchData?.VERIFY_YOUR_PHONE}
      </Typography>
      <Typography
        variant="p"
        className=" my-6 h-7 w-full text-center !font-primary-rhlight !font-[300] customXs:my-4"
      >
        {stytchData?.Enter_your_phone.split(" ").slice(0, -2).join(" ")}
        <br />
        {stytchData?.Enter_your_phone.split(" ").slice(-2).join(" ")}
      </Typography>
      <FormProvider {...form}>
        <form
          className="mt-0 flex w-80 flex-col"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <Controller
            name="phone"
            control={form.control}
            rules={{
              required: "Phone number is required",
              validate: value => value.trim() !== "" || "Invalid phone number"
            }}
            render={({ onChange, value }) => (
              <StytchPhoneField value={value} onChange={onChange} />
            )}
          />
          {errorMessage && (
            <Typography
              variant="error"
              className="!font-primary-rhlight !text-[11px]"
            >
              {errorMessage}
            </Typography>
          )}
          <Button
            type="submit"
            variant="secondary"
            disabled={loading || !isFormComplete}
            className="mt-10 h-[48px]"
          >
            {stytchData?.CONTINUE}
          </Button>
          <div className="flex justify-center">
            <Button
              className="mt-4 !h-[14px] !p-0 underline decoration-[0.5px] underline-offset-2"
              variant="link"
              onClick={handleCloseModal}
              onKeyDown={e => {
                // Ensure the button itself is focused before handling the keydown event
                if (
                  e.currentTarget === document.activeElement &&
                  (e.key === "Enter" || e.key === " ")
                ) {
                  e.preventDefault(); // Prevent default behavior
                  handleCloseModal();
                }
              }}
              tabIndex={0}
            >
              <Typography variant="caption" className="!font-primary-rhlight">
                {stytchData?.No_Thanks}
              </Typography>
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default memoize(MobileAuthentication);
